import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, newId } from '../engine/context'
import { type SubmitWorkflowPayload, setSubmission, WorkflowError } from '../engine/workflowEngine'
import { deleteRow, findRow, insertRow, readTable, writeTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { slaDays } from './slaSetting'
import { assertNoTobacco, isTobaccoMaterialRow } from './tobaccoGuard'
import { toErrorResponse } from './workflow.handler'

/**
 * Endpoint custom JAL WMS (klasifikasi CUSTOM-NESTJS / WORKFLOW-CUSTOM) yang belum ada backend-nya.
 * Route mengikuti task 03-BACKEND-API C03 (provider); kontrak request mengikuti `SubmitWorkflowDtoV2`
 * dan response `SetSubmissionResult` sesuai mv-workflow-standart §WORKFLOW-CUSTOM.
 */

/** Buffer Reorder Point (hari) — Master SLA `ROP_BUFFER`. */
const ropBufferDays = () => slaDays('ROP_BUFFER')

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const ok = (data: unknown) => [200, { statusCode: 200, success: true, data }]

const paginate = <T>(rows: T[], params: Record<string, unknown> | undefined) => {
  const page = Math.max(1, Number(params?.page ?? 1))
  const limit = Number(params?.limit ?? 25)
  const items = limit > 0 ? rows.slice((page - 1) * limit, page * limit) : rows
  return { items, meta: { total: rows.length, page, limit } }
}

const lower = (value: unknown) => String(value ?? '').toLowerCase()

/* ---------------------------------------------------------------- ROP */

export function ropRows() {
  const groups = new Map(readTable('mt_material_group').map(row => [row.id, row]))
  const units = new Map(readTable('mt_unit').map(row => [row.id, row]))
  const companies = new Map(readTable('mt_companies').map(row => [row.id, row]))
  const packs = readTable('mt_material_pack_standard').filter(row => row.status !== 'archived')
  const stock = readTable('rm_unit').filter(row => row.unit_status === 'IN_STOCK')
  const prById = new Map(readTable('purchase_request').map(row => [row.id, row]))
  const prLines = readTable('purchase_request_line')

  return (
    readTable('mt_material')
      .filter(material => material.is_active !== false)
      .map(material => {
        const saldo = stock
          .filter(unit => idOf(unit.material_id) === material.id)
          .reduce((sum, unit) => sum + Number(unit.qty_small ?? 0), 0)
        const capDay = Number(material.cap_day ?? 0)
        const longest = packs
          .filter(pack => idOf(pack.material_id) === material.id)
          .reduce<LocalRow | undefined>(
            (max, pack) => (!max || Number(pack.lead_days) > Number(max.lead_days) ? pack : max),
            undefined
          )
        const leadDays = Number(longest?.lead_days ?? 0)
        const ropSuggested = capDay ? Math.round(capDay * (leadDays + ropBufferDays())) : null
        const rop =
          material.rop_manual != null && material.rop_manual !== '' ? Number(material.rop_manual) : ropSuggested
        const openPr = prLines
          .filter(line => idOf(line.material_id) === material.id)
          .map(line => prById.get(idOf(line.pr_id)))
          .filter((pr): pr is LocalRow => Boolean(pr))
          .sort((a, b) => String(b.date_created).localeCompare(String(a.date_created)))[0]

        return {
          id: material.id,
          material_id: material.id,
          code: material.code,
          name: material.name,
          category: groups.get(idOf(material.category_id))?.name ?? null,
          unit_small: units.get(idOf(material.unit_small_id))?.name ?? null,
          purchase_unit_id: idOf(material.default_purchase_unit_id) || null,
          purchase_unit: units.get(idOf(material.default_purchase_unit_id))?.name ?? null,
          saldo_small: saldo,
          cap_day: capDay || null,
          days_of_cover: capDay ? Math.floor(saldo / capDay) : null,
          lead_days_longest: leadDays || null,
          lead_supplier_name: longest ? (companies.get(idOf(longest.supplier_id))?.name ?? null) : null,
          rop_limit: rop,
          rop_is_manual: material.rop_manual != null && material.rop_manual !== '',
          status_stok: rop == null ? '-' : saldo <= rop ? 'PESAN' : 'AMAN',
          /** D97: Tembakau masuk lewat Penerimaan Tembakau — papan ROP tidak menawarkan "Buat PR" untuknya. */
          is_tobacco: isTobaccoMaterialRow(material),
          open_pr_id: openPr?.id ?? null,
          open_pr_no: openPr?.pr_no ?? null
        }
      })
      /** Paling mendesak dulu (perkiraan habis terkecil), lalu urutan master — sama dengan papan prototype. */
      .map((row, order) => ({ row, order }))
      .sort(
        (a, b) =>
          (a.row.days_of_cover ?? Number.POSITIVE_INFINITY) - (b.row.days_of_cover ?? Number.POSITIVE_INFINITY) ||
          a.order - b.order
      )
      .map(({ row }) => row)
  )
}

/* ---------------------------------------------------------- SPK shortage */

function shortageRows() {
  const requests = new Map(readTable('material_request').map(row => [row.id, row]))
  const materials = new Map(readTable('mt_material').map(row => [row.id, row]))
  const units = new Map(readTable('mt_unit').map(row => [row.id, row]))
  const coveredLineIds = new Set(
    readTable('purchase_request_line')
      .filter(line => line.source === 'SPK_SHORTAGE' && line.source_ref_id)
      .map(line => String(line.source_ref_id))
  )

  return (
    readTable('material_request_line')
      .filter(line => line.is_shortage === true && Number(line.shortage_qty) > 0 && !coveredLineIds.has(line.id))
      /** D97: kekurangan Tembakau tidak ditutup lewat PR. */
      .filter(line => !isTobaccoMaterialRow(materials.get(idOf(line.material_id))))
      .map(line => {
        const request = requests.get(idOf(line.request_id))
        const material = materials.get(idOf(line.material_id))
        return {
          id: line.id,
          request_id: request?.id ?? null,
          request_no: request?.req_no ?? null,
          spk_no: request?.spk_no ?? null,
          material_id: material?.id ?? null,
          material_code: material?.code ?? null,
          material_name: material?.name ?? null,
          unit_id: idOf(line.unit_id) || null,
          unit: units.get(idOf(line.unit_id))?.name ?? null,
          shortage_qty: Number(line.shortage_qty),
          stock_at_request: Number(line.qty) - Number(line.shortage_qty),
          qty_requested: Number(line.qty),
          requested_at: request?.date_created ?? null
        }
      })
  )
}

/* ------------------------------------------------------- PR transactions */

type PrLineInput = {
  material_id?: string
  qty?: number
  unit_id?: string | null
  supplier_id?: string
  remark?: string | null
  source?: string | null
  source_ref_id?: string | null
}

function validateLines(lines: unknown): PrLineInput[] {
  if (!Array.isArray(lines) || lines.length === 0)
    throw new WorkflowError('Minimal satu baris material diperlukan.', 'ERR_VALIDATION_FAILED')
  for (const line of lines as PrLineInput[]) {
    if (!line.material_id || !(Number(line.qty) > 0))
      throw new WorkflowError('Setiap baris wajib memilih Material dan Qty > 0.', 'ERR_VALIDATION_FAILED')
    /** D97: Tembakau tidak pernah di PR — berlaku untuk Buat PR, Edit/Resubmit Detail PR, dan pintasan ROP / SPK. */
    assertNoTobacco([line.material_id])
    if (!line.supplier_id) {
      const material = findRow('mt_material', line.material_id)
      throw new WorkflowError(`Pilih supplier untuk: ${material?.name ?? line.material_id}`, 'ERR_VALIDATION_FAILED')
    }
  }
  return lines as PrLineInput[]
}

function lineRows(prId: string, lines: PrLineInput[]): LocalRow[] {
  return lines.map(line => {
    const material = findRow('mt_material', line.material_id)
    return {
      ...auditFieldsForCreate(),
      id: newId(),
      pr_id: prId,
      material_id: line.material_id,
      qty: Number(line.qty),
      unit_id: line.unit_id || idOf(material?.default_purchase_unit_id) || null,
      supplier_id: line.supplier_id,
      remark: line.remark || null,
      source: line.source || 'MANUAL',
      source_ref_id: line.source_ref_id || null
    } as LocalRow
  })
}

/** Nomor PR `NNN/PR/MM/YYYY`, urut per tahun (format prototype `core.prserial`). */
export function nextPrNo(now = new Date()) {
  const year = now.getFullYear()
  const highest = readTable('purchase_request')
    .map(row => /^(\d+)\/PR\/\d{2}\/(\d{4})$/.exec(String(row.pr_no)))
    .filter((match): match is RegExpExecArray => Boolean(match) && Number(match?.[2]) === year)
    .reduce((max, match) => Math.max(max, Number(match[1])), 0)
  return `${String(highest + 1).padStart(3, '0')}/PR/${String(now.getMonth() + 1).padStart(2, '0')}/${year}`
}

export function createPrAndSubmit(body: SubmitWorkflowPayload) {
  const nativePayload = body.native_document_payload?.payload ?? {}
  const lines = validateLines(nativePayload.lines)
  const prId = newId()
  insertRow('purchase_request', {
    ...auditFieldsForCreate(),
    id: prId,
    pr_no: nextPrNo(),
    status_id: null,
    submission_id: null,
    stage: null,
    rejection_reason: null
  })
  const inserted = lineRows(prId, lines).map(row => insertRow('purchase_request_line', row))
  try {
    return setSubmission({
      ...body,
      submission_id: null,
      entity_id: prId,
      native_document_payload: { path: 'purchase_request', payload: { id: prId } }
    })
  } catch (error) {
    inserted.forEach(row => deleteRow('purchase_request_line', row.id))
    deleteRow('purchase_request', prId)
    throw error
  }
}

export function replaceLinesAndSubmit(body: SubmitWorkflowPayload) {
  const nativePayload = body.native_document_payload?.payload ?? {}
  const prId = String(nativePayload.id ?? '')
  const pr = findRow('purchase_request', prId)
  if (!pr) throw new WorkflowError('Purchase Request tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const lines = validateLines(nativePayload.lines)

  /**
   * TL 12B memetakan `CMP_PRC_REVISE` ke dua aksi (RESUBMIT_SPV / RESUBMIT_PURCH). `action-config` tidak
   * menerima submission sehingga bisa mengembalikan aksi yang salah [GAP-BE-WF-02]; controller custom ini
   * memilih aksi sekomponen yang `from_status`-nya sama dengan status PR saat ini.
   */
  const requested = findRow('wml_action', body.action_id)
  const submission = findRow('wxl_submission', idOf(pr.submission_id))
  const currentStatusId = idOf(submission?.current_status_id)
  const resolvedAction =
    requested && idOf(requested.from_status_id) !== currentStatusId
      ? readTable('wml_action').find(
          action =>
            idOf(action.component_id) === idOf(requested.component_id) &&
            idOf(action.from_status_id) === currentStatusId
        )
      : requested
  const actionId = resolvedAction?.id ?? body.action_id

  const previous = readTable('purchase_request_line')
  writeTable('purchase_request_line', [...previous.filter(line => idOf(line.pr_id) !== prId), ...lineRows(prId, lines)])
  try {
    return setSubmission({
      ...body,
      action_id: actionId,
      submission_id: idOf(pr.submission_id) || body.submission_id,
      native_document_payload: { path: 'purchase_request', payload: { id: prId, rejection_reason: null } }
    })
  } catch (error) {
    writeTable('purchase_request_line', previous)
    throw error
  }
}

const parse = (data: unknown) => JSON.parse(String(data ?? '{}')) as SubmitWorkflowPayload

export function registerWmsHandlers(mock: MockAdapter) {
  mock.onGet('/api/v1/wms/inventory/rop-summary').reply(config => {
    const params = (config.params ?? {}) as Record<string, unknown>
    const all = ropRows()
    const search = lower(params.search).trim()
    const statusPr = String(params.status_pr ?? 'ALL')
    const statusStok = String(params.status_stok ?? 'ALL')
    const filtered = all.filter(
      row =>
        (!search || [row.code, row.name, row.category].some(value => lower(value).includes(search))) &&
        (statusPr !== 'NONE' || !row.open_pr_id) &&
        (statusStok === 'ALL' || row.status_stok === statusStok)
    )
    return ok({
      ...paginate(filtered, params),
      summary: {
        total: all.length,
        pesan: all.filter(row => row.status_stok === 'PESAN').length,
        aman: all.filter(row => row.status_stok === 'AMAN').length,
        actionable: all.filter(row => row.status_stok === 'PESAN' && !row.open_pr_id && !row.is_tobacco).length,
        hidden_by_filter: all.length - filtered.length
      }
    })
  })

  mock
    .onGet('/api/v1/wms/purchase-request/spk-shortage-summary')
    .reply(config => ok(paginate(shortageRows(), config.params as Record<string, unknown>)))

  mock.onPost('/api/v1/wms/daftar-pr/workflow/create-pr-modal').reply(config => {
    try {
      return ok(createPrAndSubmit(parse(config.data)))
    } catch (error) {
      return toErrorResponse(error)
    }
  })

  mock.onPost('/api/v1/wms/detail-pr/workflow/pr-header-item-editor').reply(config => {
    try {
      return ok(replaceLinesAndSubmit(parse(config.data)))
    } catch (error) {
      return toErrorResponse(error)
    }
  })
}
